import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Loader2, Mail, Plus, ScanLine, Search, Settings } from 'lucide-react';
import ReparationsDeviceCard from './ReparationsDeviceCard';
import ReparationsDetailPanel from './ReparationsDetailPanel';

const ReparationsAtelierView = ({
  api,
  t,
  loading,
  devices,
  allDevices,
  onlineCount,
  searchQuery,
  onSearchChange,
  magasinFilter,
  onMagasinFilter,
  magasinsList,
  statusFilter,
  onStatusFilter,
  statuses,
  selectedRef,
  onSelectRef,
  bulkSelectedRefs,
  onToggleBulkRef,
  onSelectAllVisible,
  onClearBulk,
  onBulkNote,
  isAdmin,
  showOnlineRequests,
  onToggleOnlineRequests,
  showStatusManager,
  onToggleStatusManager,
  onQrSearch,
  onlinePanel,
  statusManagerPanel,
  onStatusClick,
  onAcomptes,
  onAddAcompte,
  onOpenPrint,
  onTimeline,
  onNotes,
  onChat,
  onCreateDevis,
  onDelete,
}) => {
  const safeAll = Array.isArray(allDevices) ? allDevices : [];
  const safeDevices = Array.isArray(devices) ? devices : [];
  const enCours = safeAll.filter((d) =>
    ['Reçu', 'En Cours', 'Attente Pièces', 'En attente de dépôt'].includes(d.status),
  ).length;
  const termines = safeAll.filter((d) => d.status === 'Terminé' || d.status === 'Livré').length;
  const selected = safeDevices.find((d) => d.ref === selectedRef) || null;

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="mb-3 px-1 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-white uppercase tracking-wide">{t('rep.manage')}</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {enCours} en cours · {termines} terminés/livrés
            {onlineCount > 0 ? ` · ${onlineCount} demande${onlineCount > 1 ? 's' : ''} en ligne` : ''}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#151820] p-3 sm:p-4 mb-3 space-y-3">
        <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t('rep.search')}
              className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm min-h-[44px]"
              data-testid="search-devices"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs"
              >
                X
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onQrSearch}
              className="min-h-[44px] px-3 rounded-lg bg-white/10 border border-white/10 text-gray-300 text-sm font-medium flex items-center gap-2 hover:bg-white/15"
              data-testid="qr-search-btn"
            >
              <ScanLine className="w-4 h-4" /> Ref QR
            </button>
            <Link
              to="/reparations/nouveau"
              className="min-h-[44px] px-4 rounded-lg bg-red-600 hover:bg-red-500 text-white font-bold text-sm flex items-center gap-2"
              data-testid="add-device-btn"
            >
              <Plus className="w-4 h-4" /> {t('rep.new')}
            </Link>
            <button
              type="button"
              onClick={onToggleOnlineRequests}
              className={`min-h-[44px] px-3 rounded-lg font-bold text-sm flex items-center gap-2 relative ${
                showOnlineRequests ? 'bg-red-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/15'
              }`}
              data-testid="toggle-online-requests"
            >
              <Globe className="w-4 h-4" />
              En ligne
              {onlineCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {onlineCount}
                </span>
              )}
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={onToggleStatusManager}
                className="min-h-[44px] w-[44px] rounded-lg bg-white/10 text-gray-400 hover:text-white hover:bg-white/15 flex items-center justify-center"
                title="Paramètres statuts"
                data-testid="status-settings-btn"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {magasinsList.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => onMagasinFilter('')}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                  magasinFilter === '' ? 'bg-white/15 border-white/30 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                }`}
                data-testid="rep-magasin-filter-all"
              >
                Tous
              </button>
              {magasinsList.map((m) => (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => onMagasinFilter(m.code)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                    magasinFilter === m.code ? 'border-white/30' : 'border-white/10 hover:bg-white/10'
                  }`}
                  style={
                    magasinFilter === m.code
                      ? { background: `${m.color || '#10b981'}33`, color: m.color || '#10b981' }
                      : { background: 'rgba(255,255,255,0.03)', color: '#a3a3a3' }
                  }
                  data-testid={`rep-magasin-filter-${m.code}`}
                >
                  {m.code}
                </button>
              ))}
              <span className="w-px h-5 bg-white/10 mx-1" />
            </>
          )}
          <button
            type="button"
            onClick={() => onStatusFilter('')}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border ${
              !statusFilter ? 'bg-white/15 border-white/30 text-white' : 'border-white/10 text-gray-400 hover:bg-white/10'
            }`}
          >
            {t('rep.allStatuses')}
          </button>
          {statuses.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onStatusFilter(statusFilter === s ? '' : s)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                statusFilter === s ? 'bg-amber-900/40 border-amber-500/40 text-amber-200' : 'border-white/10 text-gray-400 hover:bg-white/10'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {showOnlineRequests && onlinePanel}
      {showStatusManager && statusManagerPanel}

      {safeDevices.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-[#151820] px-3 py-2">
          {(bulkSelectedRefs || []).length > 0 ? (
            <>
              <span className="text-sm font-semibold text-cyan-200">
                {(bulkSelectedRefs || []).length} dossier{(bulkSelectedRefs || []).length > 1 ? 's' : ''} coche{(bulkSelectedRefs || []).length > 1 ? 's' : ''}
              </span>
              <button
                type="button"
                onClick={onSelectAllVisible}
                className="min-h-[36px] px-3 rounded-lg text-xs font-bold bg-white/10 text-gray-300 hover:bg-white/15"
              >
                Toute la liste
              </button>
              <button
                type="button"
                onClick={onClearBulk}
                className="min-h-[36px] px-3 rounded-lg text-xs font-bold bg-white/10 text-gray-300 hover:bg-white/15"
              >
                Tout decocher
              </button>
              <button
                type="button"
                onClick={onBulkNote}
                className="min-h-[36px] px-3 rounded-lg text-xs font-bold bg-red-600 hover:bg-red-500 text-white flex items-center gap-1.5"
                data-testid="repair-bulk-note-btn"
              >
                <Mail className="w-3.5 h-3.5" />
                Envoyer une note
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onSelectAllVisible}
              className="min-h-[36px] px-3 rounded-lg text-xs font-bold bg-white/10 text-gray-300 hover:bg-white/15"
              data-testid="repair-select-all-btn"
            >
              Cocher toute la liste ({safeDevices.length})
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col xl:flex-row gap-3 min-h-[calc(100vh-280px)]">
        <div className="xl:w-[42%] shrink-0 flex flex-col min-h-0">
          {loading ? (
            <div className="rounded-xl border border-white/10 bg-[#151820] p-12 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
            </div>
          ) : devices.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-[#151820] p-12 text-center text-gray-500 text-sm">
              {t('rep.noRepairs')}
            </div>
          ) : (
            <div className="flex flex-col gap-3 overflow-y-auto max-h-[calc(100vh-280px)] pr-1 pb-1" data-testid="repairs-device-list">
              {devices.map((device) => (
                <ReparationsDeviceCard
                  key={device.id || device.ref}
                  device={device}
                  selected={device.ref === selectedRef}
                  checked={(bulkSelectedRefs || []).includes(device.ref)}
                  statuses={statuses}
                  isAdmin={isAdmin}
                  compact={false}
                  onSelect={() => onSelectRef(device.ref)}
                  onToggleCheck={onToggleBulkRef}
                  onStatusClick={onStatusClick}
                  onAcomptes={onAcomptes}
                  onAddAcompte={onAddAcompte}
                  onOpenPrint={onOpenPrint}
                  onTimeline={onTimeline}
                  onNotes={onNotes}
                  onChat={onChat}
                />
              ))}
            </div>
          )}
        </div>

        <div className="hidden xl:block xl:flex-1 rounded-xl border border-white/10 bg-[#151820] p-4 min-h-[400px]">
          <ReparationsDetailPanel
            device={selected}
            api={api}
            statuses={statuses}
            isAdmin={isAdmin}
            onStatusClick={onStatusClick}
            onOpenPrint={onOpenPrint}
            onNotes={onNotes}
            onChat={onChat}
            onCreateDevis={onCreateDevis}
            onAddAcompte={onAddAcompte}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Mobile / tablette : panneau détail en drawer géré par la page parent */}
    </div>
  );
};

export default ReparationsAtelierView;
